import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Lock } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { todayKey, shiftKey, keyLabel, fmtTime } from "@/lib/format";
import { ErrorBox, Spinner, TeamMark } from "@/components/Bits";

export default function SelectionTab({ league, owner }) {
  const { tz } = useAuth();
  const [day, setDay] = useState(todayKey());
  const [games, setGames] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    setGames(null);
    api.get("/games", { params: { league_id: league.id, date: day } }).then(({ data }) => setGames(data.games)).catch((e) => setError(errMsg(e)));
  }, [league.id, day]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (g, included) => {
    try {
      await api.put(`/leagues/${league.id}/games/${g.id}/exclusion`, { excluded: !included });
      setGames((l) => l.map((x) => (x.id === g.id ? { ...x, excluded: !included } : x)));
      toast.success(included ? "Match ajouté à la sélection" : "Match exclu de la sélection");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="selection-tab">
      <p className="mb-4 text-sm text-slate-400">
        {owner ? "Choisissez les matchs du calendrier qui comptent pour la ligue. Un match commencé ne peut plus être modifié ; chaque changement est journalisé." : "Sélection de matchs définie par le propriétaire, identique pour tous les membres."}
      </p>
      <div className="mb-4 flex items-center justify-between rounded-full border border-white/10 bg-[#05070D] p-1">
        <button onClick={() => setDay(shiftKey(day, -1))} className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-white/5" data-testid="selection-prev-day"><ChevronLeft size={18} /></button>
        <span className="text-sm font-bold capitalize">{keyLabel(day)}</span>
        <button onClick={() => setDay(shiftKey(day, 1))} className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-white/5" data-testid="selection-next-day"><ChevronRight size={18} /></button>
      </div>
      {error && <ErrorBox message={error} />}
      {!games ? <Spinner /> : (
        <div className="mpl-card divide-y divide-white/[0.05]">
          {games.length === 0 && <p className="p-4 text-sm text-slate-400">Aucun match au calendrier ce jour-là.</p>}
          {games.map((g) => (
            <div key={g.id} className="flex items-center gap-3 px-4 py-3" data-testid={`selection-row-${g.id}`}>
              <TeamMark team={g.away} size={34} /><span className="text-xs text-slate-500">@</span><TeamMark team={g.home} size={34} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{g.away.name} @ {g.home.name}</p>
                <p className="text-xs text-slate-400">{fmtTime(g.start_time, tz)} · {g.excluded ? "Exclu" : "Inclus"}</p>
              </div>
              {g.locked ? <Lock size={16} className="text-slate-500" /> : owner && (
                <Switch checked={!g.excluded} onCheckedChange={(v) => toggle(g, v)} data-testid={`selection-toggle-${g.id}`} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
